import { ElMessage } from "element-plus";
import { getComponentPath } from './componentPathMap';
import { getMenus, waitMenusReady } from '@/utils/storeMenu.ts'

/**
 * 菜单树数据项接口定义
 */
export interface MenuItem {
  path: string;
  name: string;
  component?: string;
  children?: MenuItem[];
  meta?: {
    title?: string;
    icon?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * 路由配置项接口定义
 */
export interface RouteConfig {
  path: string;
  name: string;
  component: any;
  children?: RouteConfig[];
  meta?: {
    title?: string;
    icon?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * 递归生成路由
 */
export function generateDynamicRoutes(
  menuTree: MenuItem[],
  layout: any,
  parentPath = ''
): RouteConfig[] {
  if (!Array.isArray(menuTree)) return []

  return menuTree.map((item) => {
    const fullPath = parentPath
      ? `${parentPath}${item.path}`
      : `${item.path}`;

    const route: any = {
      path: fullPath,
      name: item.name,
      meta: { title: item.meta?.title || item.name, icon: item.meta?.icon || 'Menu' }
    }
    // 有子菜单 -> 文件夹节点，用同步 layout
    if (item.children?.length) {
      route.component = layout
      route.children = generateDynamicRoutes(item.children, layout, fullPath)
    } else {
      // 叶子节点 -> 必须异步
      route.component = item.component
        ? item.component  // 后端返回了组件路径
        : getComponentPath(item.path)  // 默认用 path 当文件名（已返回 () => import() 懒加载函数）
    }
    return route
  })
}

/**
 * 从后台加载菜单树并生成路由
 *
 * @param layout 布局组件
 * @returns Promise<RouteConfig[]>
 */
export async function loadRoutesFromBackend(
  layout: any
): Promise<RouteConfig[]> {
  try {
    // 等待菜单真正写入 localStorage（登录/切换租户场景），或命中缓存立即返回（F5 场景）。
    // 替代原先写死的 500ms 盲等，消除“菜单接口未返回就读菜单”的时序竞争。
    await waitMenusReady();
    const menus = getMenus();
    const menuData = typeof menus === 'string' ? JSON.parse(menus) : menus;
    const menuTree: MenuItem[] = menuData[0].children;
    // 生成动态路由
    return generateDynamicRoutes(menuTree, layout);
  } catch (error) {
    ElMessage.error("系统错误，请联系管理员");
    return [];
  }
}

/**
 * 合并静态路由和动态路由
 *
 * @param staticRoutes 静态路由配置
 * @param dynamicRoutes 动态路由配置
 * @returns 合并后的路由配置数组
 */
export function mergeRoutes(
  staticRoutes: RouteConfig[],
  dynamicRoutes: RouteConfig[]
): RouteConfig[] {
  // 查找根节点路由
  const rootRouteIndex = staticRoutes.findIndex(
    (route) => route.path === "/root"
  );

  if (rootRouteIndex !== -1) {
    // 如果存在根节点路由，将动态路由添加为其子路由
    if (!staticRoutes[rootRouteIndex].children) {
      staticRoutes[rootRouteIndex].children = [];
    }
    staticRoutes[rootRouteIndex].children = [
      ...(staticRoutes[rootRouteIndex].children || []),
      ...dynamicRoutes,
    ];
  } else {
    // 如果不存在根节点路由，创建一个新的
    staticRoutes.push({
      path: "/root",
      name: "SAAS",
      component: staticRoutes.find(
        (route) => route.path === "/" && route.children
      )?.component,
      meta: {
        title: "SAAS",
        icon: "Menu",
      },
      children: dynamicRoutes,
    });
  }

  return staticRoutes;
}